import { useEffect, useMemo, useRef, useState, forwardRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { FORM_TRANSLATIONS, LANGUAGES } from "@/lib/checkInTranslations";
import { ALL_DOCUMENT_TYPES, COMMON_DOCS, ALPHA3_TO_ALPHA2, COUNTRY_NAME_EN, flagSrc, monthsFor, TIME_OPTIONS } from "@/lib/checkInData";
import MRZScanner, { MRZScanButton } from "@/components/MRZScanner";
import { CheckinContactFields } from "@/components/CheckinContactFields";
import {
  CircleNotch, CaretDown, MagnifyingGlass, X, IdentificationCard,
  CreditCard, BookOpen, Car, Calendar as CalendarIcon, Warning, CheckCircle,
  CaretLeft as CaretLeftIcon, CaretRight as CaretRightIcon, Plus, Trash,
} from "@phosphor-icons/react";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

// =============================================================================
// applyFormStyle — write CSS vars onto :root so the whole form themes itself
// =============================================================================
function applyFormStyle(style) {
  if (!style) return;
  const r = document.documentElement;
  const map = {
    "--cf-primary": style.primary_color,
    "--cf-primary-hover": style.primary_hover,
    "--cf-bg": style.background_color,
    "--cf-card-bg": style.card_background,
    "--cf-text": style.text_color,
    "--cf-text-muted": style.text_muted,
    "--cf-border": style.border_color,
    "--cf-warning-bg": style.warning_bg,
    "--cf-warning-text": style.warning_text,
    "--cf-radius": (style.radius || "12") + "px",
    "--cf-heading-font": style.heading_font ? `'${style.heading_font}', serif` : "'Playfair Display', serif",
    "--cf-body-font": style.body_font ? `'${style.body_font}', sans-serif` : "'Inter', sans-serif",
  };
  Object.entries(map).forEach(([k, v]) => { if (v) r.style.setProperty(k, v); });
  // Load Google Fonts
  const fonts = [style.heading_font, style.body_font].filter(Boolean);
  if (fonts.length) {
    const id = "cf-google-fonts";
    let link = document.getElementById(id);
    if (!link) {
      link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    const families = fonts.map((f) => `family=${encodeURIComponent(f).replace(/%20/g, "+")}:wght@400;500;600;700`).join("&");
    link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
  }
}

// =============================================================================
// Reusable styled components
// =============================================================================
const FormInput = forwardRef(function FormInput(props, ref) {
  return <input ref={ref} {...props} className={`cf-input ${props.className || ""}`} />;
});

function FormLabel({ label, required, children, hint, error }) {
  return (
    <label className="block">
      <span className="mb-2 block text-[13px] font-semibold" style={{ color: "var(--cf-text)" }}>
        {label}{required && <span className="ml-0.5 text-rose-500">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-[11px]" style={{ color: "var(--cf-text-muted)" }}>{hint}</span>}
      {error && <span className="mt-1 block text-[11px] text-rose-500">{error}</span>}
    </label>
  );
}

// =============================================================================
// DOB Calendar — Popover with year+month dropdowns above a grid
// =============================================================================
function DateField({ value, onChange, placeholder, lang, allowFuture = false, minDate = null, testId }) {
  const [open, setOpen] = useState(false);
  const today = new Date();
  const init = value ? new Date(value) : new Date(today.getFullYear() - 20, 0, 1);
  const [view, setView] = useState(new Date(init.getFullYear(), init.getMonth(), 1));
  const wrapRef = useRef(null);

  const close = () => setOpen(false);

  // Outside click + ESC to close (use 'click' so React's onClick fires first)
  useEffect(() => {
    if (!open) return;
    const onClk = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("click", onClk);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onClk);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const months = monthsFor(lang);
  // Normalise minDate to start-of-day for safe comparison
  const minD = useMemo(() => {
    if (!minDate) return null;
    const d = minDate instanceof Date ? new Date(minDate) : new Date(minDate);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [minDate]);
  const years = useMemo(() => {
    const arr = [];
    const max = allowFuture ? today.getFullYear() + 3 : today.getFullYear();
    const min = minD ? minD.getFullYear() : 1900;
    for (let y = max; y >= min; y--) arr.push(y);
    return arr;
  }, [allowFuture, minD]);

  const startDay = (new Date(view.getFullYear(), view.getMonth(), 1).getDay() + 6) % 7; // Mon-start
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selected = value ? new Date(value) : null;
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const display = (s) => {
    if (!s) return "";
    const [y, m, d] = s.split("-");
    return `${d}.${m}.${y}.`;
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="cf-input flex w-full items-center gap-2 text-left"
        style={{ position: "relative", zIndex: open ? 41 : 1 }}
        data-testid={testId}
      >
        <CalendarIcon size={16} style={{ color: "var(--cf-primary)" }} />
        <span className={value ? "" : "text-[var(--cf-text-muted)]"}>{value ? display(value) : placeholder}</span>
      </button>
      {open && (
        <div className="absolute z-40 mt-2 w-[330px] rounded-xl border bg-white p-3 shadow-2xl" style={{ borderColor: "var(--cf-border)" }}>
            <div className="grid grid-cols-2 gap-2">
              <select
                className="cf-input !py-1.5"
                value={view.getMonth()}
                onChange={(e) => setView(new Date(view.getFullYear(), parseInt(e.target.value), 1))}
                data-testid={`${testId}-month`}
              >
                {months.map((m, i) => <option key={i} value={i}>{m}</option>)}
              </select>
              <select
                className="cf-input !py-1.5"
                value={view.getFullYear()}
                onChange={(e) => setView(new Date(parseInt(e.target.value), view.getMonth(), 1))}
                data-testid={`${testId}-year`}
              >
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs font-semibold" style={{ color: "var(--cf-text-muted)" }}>
              <button type="button" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))} className="rounded p-1 hover:bg-black/5">
                <CaretLeftIcon size={14} />
              </button>
              <span>{months[view.getMonth()]} {view.getFullYear()}</span>
              <button type="button" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))} className="rounded p-1 hover:bg-black/5">
                <CaretRightIcon size={14} />
              </button>
            </div>
            <div className="mt-2 grid grid-cols-7 gap-0.5 text-center text-[11px] font-medium" style={{ color: "var(--cf-text-muted)" }}>
              {["P", "U", "S", "Č", "P", "S", "N"].map((d, i) => <div key={i} className="py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-0.5">
              {cells.map((d, i) => {
                if (d == null) return <div key={i} />;
                const dObj = new Date(view.getFullYear(), view.getMonth(), d);
                const iso = fmt(dObj);
                const isSel = selected && fmt(selected) === iso;
                const future = !allowFuture && dObj > today;
                const tooEarly = minD && dObj < minD;
                const blocked = future || tooEarly;
                return (
                  <button
                    type="button"
                    key={i}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!blocked) {
                        onChange(iso);
                        setTimeout(() => setOpen(false), 0);
                      }
                    }}
                    disabled={blocked}
                    className={`aspect-square rounded text-xs font-medium transition ${blocked ? "cursor-not-allowed opacity-30" : "hover:bg-[var(--cf-primary)]/10"} ${isSel ? "!bg-[var(--cf-primary)] !text-white" : ""}`}
                    style={{ color: isSel ? "white" : "var(--cf-text)" }}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>
      )}
    </div>
  );
}

// =============================================================================
// Country select — popover with search + flags
// =============================================================================
function CountrySelect({ value, onChange, countries, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClk = (e) => { if (!wrapRef.current?.contains(e.target)) { setOpen(false); setQ(""); } };
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); setQ(""); } };
    document.addEventListener("click", onClk);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onClk);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return countries;
    return countries.filter((c) =>
      c.name.toLowerCase().includes(qq) ||
      (COUNTRY_NAME_EN[c.code] || "").toLowerCase().includes(qq) ||
      c.code.toLowerCase().includes(qq)
    );
  }, [q, countries]);
  const selected = countries.find((c) => c.code === value);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="cf-input flex w-full items-center justify-between gap-2 text-left"
        style={{ position: "relative", zIndex: open ? 41 : 1 }}
        data-testid={testId}
      >
        <span className="flex items-center gap-2 truncate">
          {selected ? (
            <>
              {flagSrc(selected.code) && <img src={flagSrc(selected.code)} alt="" className="h-3.5 w-5 rounded-sm object-cover shadow-sm" />}
              <span>{selected.name}</span>
            </>
          ) : (
            <span className="text-[var(--cf-text-muted)]">{placeholder}</span>
          )}
        </span>
        <CaretDown size={14} className="shrink-0" style={{ color: "var(--cf-text-muted)" }} />
      </button>
      {open && (
          <div className="absolute z-40 mt-2 w-full rounded-xl border bg-white shadow-2xl" style={{ borderColor: "var(--cf-border)" }}>
            <div className="relative border-b p-2" style={{ borderColor: "var(--cf-border)" }}>
              <MagnifyingGlass size={14} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: "var(--cf-text-muted)" }} />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Pretraži / Search..."
                className="cf-input !pl-7 !py-1.5"
                data-testid={`${testId}-search`}
              />
            </div>
            <div className="max-h-72 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-[var(--cf-text-muted)]">Nema rezultata</div>
              ) : filtered.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(c.code);
                    setQ("");
                    setTimeout(() => setOpen(false), 0);
                  }}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--cf-primary)]/10 ${c.code === value ? "bg-[var(--cf-primary)]/5 font-semibold" : ""}`}
                  data-testid={`${testId}-opt-${c.code}`}
                >
                  {flagSrc(c.code) && <img src={flagSrc(c.code)} alt="" className="h-3.5 w-5 rounded-sm object-cover shadow-sm" />}
                  <span style={{ color: "var(--cf-text)" }}>{c.name}</span>
                  <span className="ml-auto text-[10px]" style={{ color: "var(--cf-text-muted)" }}>{COUNTRY_NAME_EN[c.code]}</span>
                </button>
              ))}
            </div>
          </div>
      )}
    </div>
  );
}

// =============================================================================
// Document type modal — 6 cards + expandable list
// =============================================================================
function DocumentTypeModal({ value, onChange, onClose }) {
  const [search, setSearch] = useState("");
  const [otherOpen, setOtherOpen] = useState(false);
  const commonDocs = COMMON_DOCS.map((c) => ALL_DOCUMENT_TYPES.find((d) => d.code === c)).filter(Boolean);
  const others = ALL_DOCUMENT_TYPES.filter((d) => !COMMON_DOCS.includes(d.code))
    .filter((d) => d.name.toLowerCase().includes(search.toLowerCase()));

  const iconFor = (group) => {
    if (group === "id") return <CreditCard size={22} weight="duotone" style={{ color: "var(--cf-primary)" }} />;
    if (group === "passport") return <BookOpen size={22} weight="duotone" style={{ color: "var(--cf-primary)" }} />;
    if (group === "driver") return <Car size={22} weight="duotone" style={{ color: "var(--cf-primary)" }} />;
    return <IdentificationCard size={22} weight="duotone" style={{ color: "var(--cf-primary)" }} />;
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 px-4">
        <div className="rounded-2xl bg-white p-5 shadow-2xl">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-bold" style={{ color: "var(--cf-text)", fontFamily: "var(--cf-heading-font)" }}>Odaberi vrstu dokumenta</h3>
            <button type="button" onClick={onClose} className="rounded-full p-1.5 hover:bg-black/5" data-testid="doc-modal-close">
              <X size={18} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {commonDocs.map((d) => (
              <button
                key={d.code}
                type="button"
                onClick={() => { onChange(d.code); onClose(); }}
                className={`flex flex-col items-start gap-2 rounded-xl border-2 p-4 text-left transition hover:shadow-md ${value === d.code ? "ring-2 ring-offset-1" : ""}`}
                style={{
                  borderColor: value === d.code ? "var(--cf-primary)" : "var(--cf-border)",
                  background: value === d.code ? "color-mix(in oklab, var(--cf-primary) 8%, white)" : "white",
                }}
                data-testid={`doc-common-${d.code}`}
              >
                {iconFor(d.group)}
                <span className="text-sm font-semibold leading-tight" style={{ color: "var(--cf-text)" }}>{d.name}</span>
              </button>
            ))}
          </div>

          <div className="mt-5 border-t pt-4" style={{ borderColor: "var(--cf-border)" }}>
            <button
              type="button"
              onClick={() => setOtherOpen((o) => !o)}
              className="flex w-full items-center justify-between text-sm font-semibold"
              style={{ color: "var(--cf-text)" }}
              data-testid="doc-other-toggle"
            >
              <span>Ostali dokumenti</span>
              <CaretDown size={14} className={`transition-transform ${otherOpen ? "rotate-180" : ""}`} />
            </button>
            {otherOpen && (
              <div className="mt-3">
                <input
                  className="cf-input mb-2"
                  placeholder="Pretraži dokumente..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="doc-other-search"
                />
                <div className="max-h-52 space-y-1 overflow-y-auto">
                  {others.map((d) => (
                    <button
                      key={d.code}
                      type="button"
                      onClick={() => { onChange(d.code); onClose(); }}
                      className={`w-full rounded-lg border p-2.5 text-left text-sm transition hover:bg-[var(--cf-primary)]/5 ${value === d.code ? "border-[var(--cf-primary)] bg-[var(--cf-primary)]/5" : ""}`}
                      style={{ borderColor: value === d.code ? "var(--cf-primary)" : "var(--cf-border)", color: "var(--cf-text)" }}
                      data-testid={`doc-other-${d.code}`}
                    >
                      {d.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// =============================================================================
// Guest section
// =============================================================================
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const emptyGuest = () => ({
  first_name: "", last_name: "", date_of_birth: "", gender: "",
  citizenship: "", country_of_birth: "",
  country_of_residence: "", city_of_residence: "",
  document_type: "", document_number: "",
  arrival_date: todayISO(), arrival_time: "15:00",
  departure_date: todayISO(), departure_time: "10:00",
});

function GuestSection({ idx, guest, onChange, onRemove, canRemove, t, meta, lang, errorFields, fieldRefs }) {
  const f = guest;
  const upd = (k, v) => onChange({ ...f, [k]: v });
  const [docModal, setDocModal] = useState(false);
  const docLabel = useMemo(() => ALL_DOCUMENT_TYPES.find((d) => d.code === f.document_type)?.name, [f.document_type]);
  const errKey = (k) => `${idx}.${k}`;
  const hasErr = (k) => errorFields?.has(errKey(k));
  const setRef = (k) => (el) => { if (fieldRefs && el) fieldRefs.current[errKey(k)] = el; };

  return (
    <div className="space-y-5">
      {idx > 0 && (
        <div className="flex items-center justify-between border-t pt-5" style={{ borderColor: "var(--cf-border)" }}>
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: "var(--cf-primary)" }}>{idx + 1}</span>
            <h3 className="text-base font-semibold" style={{ color: "var(--cf-text)", fontFamily: "var(--cf-heading-font)" }}>{t.fields.guest_index(idx + 1)}</h3>
          </div>
          <button type="button" onClick={onRemove} className="text-xs font-semibold text-rose-600 hover:underline" data-testid={`remove-guest-${idx}`}>
            <Trash size={14} className="inline" /> {t.fields.remove_guest}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
        <FormLabel label={t.fields.first_name} required>
          <FormInput ref={setRef("first_name")} className={hasErr("first_name") ? "cf-invalid" : ""} value={f.first_name} onChange={(e) => upd("first_name", e.target.value)} data-testid={`guest-${idx}-first_name`} />
        </FormLabel>
        <FormLabel label={t.fields.last_name} required>
          <FormInput ref={setRef("last_name")} className={hasErr("last_name") ? "cf-invalid" : ""} value={f.last_name} onChange={(e) => upd("last_name", e.target.value)} data-testid={`guest-${idx}-last_name`} />
        </FormLabel>

        <FormLabel label={t.fields.date_of_birth} required>
          <div ref={setRef("date_of_birth")} className={hasErr("date_of_birth") ? "cf-invalid-wrap" : ""}>
            <DateField value={f.date_of_birth} onChange={(v) => upd("date_of_birth", v)} placeholder={t.fields.select_date} lang={lang} testId={`guest-${idx}-dob`} />
          </div>
        </FormLabel>
        <FormLabel label={t.fields.gender} required>
          <select ref={setRef("gender")} className={`cf-input ${hasErr("gender") ? "cf-invalid" : ""}`} value={f.gender} onChange={(e) => upd("gender", e.target.value)} data-testid={`guest-${idx}-gender`}>
            <option value="">{t.fields.gender_select}</option>
            <option value="M">{t.fields.gender_m}</option>
            <option value="F">{t.fields.gender_f}</option>
          </select>
        </FormLabel>

        <FormLabel label={t.fields.citizenship} required>
          <div ref={setRef("citizenship")} className={hasErr("citizenship") ? "cf-invalid-wrap" : ""}>
            <CountrySelect value={f.citizenship} onChange={(v) => upd("citizenship", v)} countries={meta.countries} placeholder={t.fields.select_country} testId={`guest-${idx}-citizenship`} />
          </div>
        </FormLabel>
        <FormLabel label={t.fields.country_of_birth}>
          <CountrySelect value={f.country_of_birth} onChange={(v) => upd("country_of_birth", v)} countries={meta.countries} placeholder={t.fields.select_country} testId={`guest-${idx}-cob`} />
        </FormLabel>

        <FormLabel label={t.fields.document_type} required>
          <button ref={setRef("document_type")} type="button" onClick={() => setDocModal(true)} className={`cf-input flex w-full items-center justify-between gap-2 text-left ${hasErr("document_type") ? "cf-invalid" : ""}`} data-testid={`guest-${idx}-doctype`}>
            <span className={docLabel ? "" : "text-[var(--cf-text-muted)]"}>{docLabel || t.fields.select_doc}</span>
            <IdentificationCard size={16} style={{ color: "var(--cf-primary)" }} />
          </button>
        </FormLabel>
        <FormLabel label={t.fields.document_number} required>
          <FormInput ref={setRef("document_number")} className={hasErr("document_number") ? "cf-invalid" : ""} value={f.document_number} onChange={(e) => upd("document_number", e.target.value.toUpperCase())} data-testid={`guest-${idx}-docnum`} placeholder="ABC123456" />
        </FormLabel>

        <FormLabel label={t.fields.country_of_residence} required>
          <div ref={setRef("country_of_residence")} className={hasErr("country_of_residence") ? "cf-invalid-wrap" : ""}>
            <CountrySelect value={f.country_of_residence} onChange={(v) => upd("country_of_residence", v)} countries={meta.countries} placeholder={t.fields.select_country} testId={`guest-${idx}-cor`} />
          </div>
        </FormLabel>
        <FormLabel label={t.fields.city_of_residence} required>
          <FormInput ref={setRef("city_of_residence")} className={hasErr("city_of_residence") ? "cf-invalid" : ""} value={f.city_of_residence} onChange={(e) => upd("city_of_residence", e.target.value)} placeholder="Zagreb" data-testid={`guest-${idx}-city`} />
        </FormLabel>
      </div>

      {idx === 0 && (
        <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
          <FormLabel label={t.fields.arrival} required hint={`${t.fields.checkin_from} 15:00`}>
            <div className="grid grid-cols-[1fr,7rem] gap-2">
              <DateField
                value={f.arrival_date}
                onChange={(v) => {
                  // If departure becomes earlier than the new arrival, push it forward
                  if (f.departure_date && v && f.departure_date < v) {
                    onChange({ ...f, arrival_date: v, departure_date: v });
                  } else {
                    upd("arrival_date", v);
                  }
                }}
                placeholder={t.fields.select_date}
                lang={lang}
                allowFuture
                minDate={new Date()}
                testId={`guest-${idx}-arr-date`}
              />
              <select className="cf-input" value={f.arrival_time} onChange={(e) => upd("arrival_time", e.target.value)} data-testid={`guest-${idx}-arr-time`}>
                {TIME_OPTIONS.map((t2) => <option key={t2} value={t2}>{t2}</option>)}
              </select>
            </div>
          </FormLabel>
          <FormLabel label={t.fields.departure} required hint={`${t.fields.checkout_until} 10:00`}>
            <div className="grid grid-cols-[1fr,7rem] gap-2">
              <DateField value={f.departure_date} onChange={(v) => upd("departure_date", v)} placeholder={t.fields.select_date} lang={lang} allowFuture minDate={f.arrival_date ? new Date(f.arrival_date) : new Date()} testId={`guest-${idx}-dep-date`} />
              <select className="cf-input" value={f.departure_time} onChange={(e) => upd("departure_time", e.target.value)} data-testid={`guest-${idx}-dep-time`}>
                {TIME_OPTIONS.map((t2) => <option key={t2} value={t2}>{t2}</option>)}
              </select>
            </div>
          </FormLabel>
        </div>
      )}

      {docModal && <DocumentTypeModal value={f.document_type} onChange={(v) => upd("document_type", v)} onClose={() => setDocModal(false)} />}
    </div>
  );
}

// =============================================================================
// Main page
// =============================================================================
export default function PublicCheckInPage() {
  const params = useParams();
  const { tenantSlug, propertyId, token } = params;
  const [meta, setMeta] = useState(null);
  const [lang, setLang] = useState("hr");
  const [guests, setGuests] = useState([{ ...emptyGuest() }]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [contact, setContact] = useState({ phone: "", email: "", address: "", postal_code: "", oib: "" });
  const [error, setError] = useState("");
  const [errorFields, setErrorFields] = useState(new Set());
  const [mrzOpen, setMrzOpen] = useState(false);
  const fieldRefs = useRef({});

  const isMobile = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const ua = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const touch = !!(navigator.maxTouchPoints && navigator.maxTouchPoints > 1);
    return ua || touch;
  }, []);

  useEffect(() => {
    document.title = "Prijava gostiju";
    const url = token
      ? `${API_BASE}/public/check-in/r/${token}/meta`
      : propertyId
        ? `${API_BASE}/public/check-in/${tenantSlug}/p/${propertyId}/meta`
        : `${API_BASE}/public/check-in/${tenantSlug}/meta`;
    axios.get(url).then((r) => {
      setMeta(r.data);
      applyFormStyle(r.data.tenant?.checkin_form_style);
      // Pre-fill arrival/departure dates from the reservation if present
      const resv = r.data?.reservation;
      if (resv?.check_in && resv?.check_out) {
        setGuests((arr) => arr.map((g, i) => i === 0 ? {
          ...g,
          arrival_date: resv.check_in,
          departure_date: resv.check_out,
          first_name: g.first_name || (resv.primary_guest_name?.split(" ")[0] || ""),
          last_name: g.last_name || (resv.primary_guest_name?.split(" ").slice(1).join(" ") || ""),
        } : g));
      }
    }).catch((e) => {
      const status = e?.response?.status;
      if (status === 410) setError("Ovaj check-in link više nije aktivan (rezervacija je otkazana ili je istekla).");
      else if (status === 404) setError("Smještaj nije pronađen.");
      else setError("Greška pri učitavanju forme.");
    });
  }, [tenantSlug, propertyId, token]);

  const t = useMemo(() => FORM_TRANSLATIONS[lang] || FORM_TRANSLATIONS.hr, [lang]);
  // Token links (/check-in/r/:token) have no slug in the URL — resolve from meta
  const slugForLinks = tenantSlug || meta?.tenant?.slug;
  const propIdForLinks = propertyId || meta?.property?.id;
  const privacyPath = `/check-in/${slugForLinks}${propIdForLinks ? `/p/${propIdForLinks}` : ""}/privacy?lang=${lang}`;

  const updateGuest = (idx, g) => setGuests((arr) => arr.map((x, i) => (i === idx ? g : x)));
  const addGuest = () => {
    const lead = guests[0];
    setGuests((arr) => [...arr, { ...emptyGuest(), arrival_date: lead.arrival_date, arrival_time: lead.arrival_time, departure_date: lead.departure_date, departure_time: lead.departure_time }]);
  };
  const removeGuest = (idx) => setGuests((arr) => arr.filter((_, i) => i !== idx));

  // Populate first guest from MRZ scan result
  const onMRZComplete = (mrz) => {
    setGuests((arr) => arr.map((g, i) => i === 0 ? {
      ...g,
      first_name: mrz.firstName || g.first_name,
      last_name: mrz.lastName || g.last_name,
      date_of_birth: mrz.dateOfBirth || g.date_of_birth,
      gender: mrz.gender || g.gender,
      citizenship: mrz.nationality || g.citizenship,
      country_of_birth: mrz.countryOfBirth || g.country_of_birth,
      country_of_residence: mrz.countryOfResidence || g.country_of_residence,
      document_type: mrz.documentType || g.document_type,
      document_number: mrz.documentNumber || g.document_number,
    } : g));
  };

  const submit = async () => {
    setError("");
    if (contact.email.trim() && !/^\S+@\S+\.\S+$/.test(contact.email.trim())) {
      setError(lang === "en" ? "Please enter a valid e-mail." : lang === "de" ? "Bitte gültige E-Mail eingeben." : lang === "it" ? "Inserire un'e-mail valida." : "Unesite ispravan e-mail.");
      return;
    }
    const errs = new Set();
    const required = ["first_name", "last_name", "date_of_birth", "gender", "citizenship", "country_of_residence", "city_of_residence", "document_type", "document_number"];
    guests.forEach((g, i) => {
      required.forEach((k) => { if (!g[k]?.toString().trim()) errs.add(`${i}.${k}`); });
    });
    const lead = guests[0];
    if (!lead.arrival_date) errs.add("0.arrival_date");
    if (!lead.departure_date) errs.add("0.departure_date");
    setErrorFields(errs);
    if (errs.size > 0) {
      setError(lang === "en" ? "Please fill in all required fields." : lang === "de" ? "Bitte füllen Sie alle Pflichtfelder aus." : lang === "it" ? "Si prega di compilare tutti i campi obbligatori." : "Molimo popunite sva obavezna polja.");
      // Scroll to the first invalid field
      const first = Array.from(errs)[0];
      setTimeout(() => {
        const el = fieldRefs.current?.[first];
        if (el?.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          if (el.focus) el.focus({ preventScroll: true });
        }
      }, 80);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        lang,
        contact_phone: contact.phone.trim() || null,
        contact_email: contact.email.trim() || null,
        contact_address: contact.address.trim() || null,
        contact_postal_code: contact.postal_code.trim() || null,
        contact_oib: contact.oib.trim() || null,
        guests: guests.map((g, i) => ({
          ...g,
          arrival_date: lead.arrival_date,
          arrival_time: lead.arrival_time,
          departure_date: lead.departure_date,
          departure_time: lead.departure_time,
          is_lead_guest: i === 0,
        })),
      };
      const url = token
        ? `${API_BASE}/public/check-in/r/${token}`
        : propertyId
          ? `${API_BASE}/public/check-in/${tenantSlug}/p/${propertyId}`
          : `${API_BASE}/public/check-in/${tenantSlug}`;
      await axios.post(url, payload);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Greška");
    } finally {
      setSubmitting(false);
    }
  };

  if (!meta) {
    if (error) {
      return (
        <div className="flex min-h-screen items-center justify-center px-6" data-testid="checkin-load-error">
          <p className="max-w-md text-center text-base text-slate-700">{error}</p>
        </div>
      );
    }
    return <div className="flex min-h-screen items-center justify-center"><CircleNotch size={32} className="animate-spin" /></div>;
  }

  const tenant = meta.tenant;
  const formStyle = tenant?.checkin_form_style || {};
  const customLogo = formStyle.logo_url && formStyle.logo_url.trim() ? formStyle.logo_url : null;
  const useLogoUrl = customLogo || (formStyle.use_pms_logo !== false ? tenant?.logo_url : null);
  const LANG_FLAG = { hr: "HRV", en: "GBR", de: "DEU", it: "ITA" };
  const cardTitle = meta.property?.checkin_title || tenant?.brand_name || tenant?.name || "Prijava gostiju";

  return (
    <div className="min-h-screen pb-12" style={{ background: "var(--cf-bg, #f5f5f0)", fontFamily: "var(--cf-body-font)" }} data-testid="public-checkin-page">
      {/* Header */}
      <header>
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-4 px-5 py-5">
          {useLogoUrl ? (
            <img src={useLogoUrl} alt={tenant?.brand_name} className="h-14 w-auto" data-testid="form-logo" />
          ) : (
            <div className="text-2xl font-bold" style={{ color: "var(--cf-text)", fontFamily: "var(--cf-heading-font)" }}>{tenant?.brand_name || tenant?.name}</div>
          )}
          <div className="flex gap-1.5" data-testid="lang-switcher">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-bold transition ${lang === l.code ? "border-[var(--cf-primary)] bg-[var(--cf-primary)] text-white" : "bg-white"}`}
                style={{ borderColor: lang === l.code ? "var(--cf-primary)" : "var(--cf-border)", color: lang === l.code ? "white" : "var(--cf-text)" }}
                data-testid={`lang-${l.code}`}
              >
                <img src={flagSrc(LANG_FLAG[l.code])} alt="" className="h-3.5 w-5 rounded-sm object-cover shadow-sm" />
                {l.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4">
        {success ? (
          <div className="rounded-[var(--cf-radius)] p-10 text-center shadow-lg" style={{ background: "var(--cf-card-bg)" }} data-testid="checkin-success">
            <CheckCircle size={56} weight="fill" className="mx-auto mb-4" style={{ color: "var(--cf-primary)" }} />
            <h2 className="mb-2 text-2xl font-bold" style={{ color: "var(--cf-text)", fontFamily: "var(--cf-heading-font)" }}>{t.success_title}</h2>
            <p className="text-sm" style={{ color: "var(--cf-text-muted)" }}>{t.success_subtitle}</p>
          </div>
        ) : (
          <div className="rounded-[var(--cf-radius)] px-6 py-8 shadow-lg sm:px-10" style={{ background: "var(--cf-card-bg)" }}>
            <div className="mb-7 text-center">
              <h1 className="text-3xl font-bold tracking-tight sm:text-4xl" style={{ color: "var(--cf-text)", fontFamily: "var(--cf-heading-font)" }} data-testid="form-title">{cardTitle}</h1>
              <div className="mx-auto mt-3 h-0.5 w-16" style={{ background: "var(--cf-primary)" }} />
              <p className="mx-auto mt-4 max-w-xl text-sm" style={{ color: "var(--cf-text)" }}>{t.subtitle}</p>
              <p className="mx-auto mt-2 max-w-xl text-xs" style={{ color: "var(--cf-text-muted)" }}>{t.legal_intro}</p>
            </div>

            {error && (
              <div className="mb-5 flex items-start gap-2 rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-700" data-testid="form-error">
                <Warning size={16} weight="fill" className="mt-0.5 shrink-0" /> {error}
              </div>
            )}

            <div className="space-y-6">
              {isMobile && (
                <MRZScanButton onOpen={() => setMrzOpen(true)} lang={lang} style={formStyle} />
              )}
              {guests.map((g, i) => (
                <GuestSection key={i} idx={i} guest={g} onChange={(ng) => updateGuest(i, ng)} onRemove={() => removeGuest(i)} canRemove={i > 0} t={t} meta={meta} lang={lang} errorFields={errorFields} fieldRefs={fieldRefs} />
              ))}
            </div>

            <CheckinContactFields lang={lang} value={contact} onChange={setContact} />

            <div
              className="mt-6 flex items-start gap-3 rounded-xl border p-4 text-sm"
              style={{ background: "var(--cf-warning-bg)", borderColor: "color-mix(in oklab, var(--cf-warning-text) 30%, white)", color: "var(--cf-warning-text)" }}
              data-testid="form-notice"
            >
              <Warning size={20} weight="fill" className="mt-0.5 shrink-0" />
              <div><span className="font-bold">Važno:</span> {t.important.replace("Važno: ", "")}</div>
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={addGuest}
                className="rounded-[var(--cf-radius)] border-2 px-6 py-3 text-sm font-bold transition hover:bg-[var(--cf-primary)]/5"
                style={{ borderColor: "var(--cf-primary)", color: "var(--cf-primary)" }}
                data-testid="add-guest-btn"
              >
                <Plus size={14} weight="bold" className="inline" /> {t.add_guest.replace(/^\+ ?/, "").trim()}
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="flex-1 rounded-[var(--cf-radius)] px-6 py-3 text-base font-bold text-white shadow-md transition hover:shadow-lg disabled:opacity-50"
                style={{ background: "var(--cf-primary)" }}
                data-testid="submit-btn"
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--cf-primary-hover)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "var(--cf-primary)")}
              >
                {submitting ? (<><CircleNotch size={14} className="inline animate-spin" /> {t.submitting}</>) : t.submit(guests.length)}
              </button>
            </div>

            <p className="mt-6 text-center text-xs leading-relaxed" style={{ color: "var(--cf-text-muted)" }}>
              {t.consent}{" "}
              <Link to={privacyPath} data-testid="checkin-privacy-link" className="font-semibold underline" style={{ color: "var(--cf-primary)" }}>{t.privacy_link}</Link>
            </p>
          </div>
        )}
      </main>

      <footer className="mt-10 text-center text-xs" style={{ color: "var(--cf-text-muted)" }}>
        Powered by <span className="font-bold" style={{ color: "var(--cf-text)" }}>Lodgy</span>
      </footer>

      <MRZScanner isOpen={mrzOpen} onClose={() => setMrzOpen(false)} onScanComplete={onMRZComplete} lang={lang} />

      <style>{`
        :root { --cf-bg: #FFFEFF; --cf-primary: #FF990A; --cf-primary-hover: #E68600; --cf-card-bg: #fff; --cf-text: #001A38; --cf-text-muted: #19304B; --cf-border: #E2E8F0; --cf-warning-bg: #FFF3E0; --cf-warning-text: #FF990A; --cf-radius: 10px; --cf-heading-font: 'Inter', sans-serif; --cf-body-font: 'Inter', sans-serif; }
        body { background: var(--cf-bg); }
        .cf-input {
          width: 100%;
          background: var(--cf-card-bg, white);
          border: 1px solid var(--cf-border);
          padding: 0.75rem 0.875rem;
          font-size: 0.9rem;
          border-radius: calc(var(--cf-radius) * 0.6);
          color: var(--cf-text);
          font-family: var(--cf-body-font);
          transition: border-color .15s, box-shadow .15s;
        }
        .cf-input:hover { border-color: color-mix(in oklab, var(--cf-primary) 40%, var(--cf-border)); }
        .cf-input:focus { outline: none; border-color: var(--cf-primary); box-shadow: 0 0 0 3px color-mix(in oklab, var(--cf-primary) 18%, transparent); }
        .cf-invalid { border-color: #ef4444 !important; background: #fff5f5 !important; box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.18); }
        .cf-invalid-wrap > * { border-color: #ef4444 !important; background: #fff5f5 !important; box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.18); }
      `}</style>
    </div>
  );
}
